import React, { useCallback, useEffect, useState } from "react";
import HeaderPanel from "../layout/HeaderPanel";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { detachMultilinkedDocumentPartAsync, normalizePath } from "../../redux/feature/document/document-slice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import {
  selectBackendUrl,
  selectClientId,
  selectCpmUsername,
  selectTenantId,
} from "../../redux/feature/settings/configurations-slice";
import { elaborationStatusItemIsBlocking } from "../../redux/feature/object/remote-elaborations-slice";
import LoadingManager from "../../common/loading/LoadingManager";
import { DocumentData, EditDocument } from "./EditDocument";
import {
  fetchDocumentMetadata,
  fetchDocumentPartDetails,
  fetchParentsDocumentPart,
} from "../../api/document/DocumentsPart";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";
import { DETACH_ELABORATION } from "../../utils/constants/notification";
import { defaultFormatting } from "../../utils/constants/formats";
import { useFilteredElaborations } from "../generic/GenericElaborationSelector";
import { ElaborationWrapper } from "../coobjects/ElaborationWrapper";

interface DetachProps {
  documentId: string;
  partOrder: number;
  fatherDocumentId: string;
  onCloseModal: () => void;
  channelIdToNotify: string;
}

export const DetachDocumentPanel: React.FunctionComponent<DetachProps> = (props) => {
  const username = useAppSelector(selectCpmUsername); // TEMP waiting for real login
  const backendUrl = useAppSelector(selectBackendUrl);
  const tenantId = useAppSelector(selectTenantId);
  const clientId = useAppSelector(selectClientId);
  const dispatch = useAppDispatch();

  const detachDocument = async (data: DocumentData) => {
    await detachMultilinkedDocumentPartAsync({
      isDetachWithSingleParent,
      documentId: props.documentId,
      partOrder: props.partOrder,
      fatherDocumentId: props.fatherDocumentId,
      channelIdToNotify: props.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      sharepointPath: normalizePath(data.path),
      username,
      tenantId,
      dispatch,
      backendUrl,
      documentPartMetadata: data.parameters,
      documentName: data.name,
      clientId,
      language: data.language,
      templateInfo: data.advanced.templateInfo,
      locale: data.advanced.locale,
    });
    props.onCloseModal();
  };

  const [isDetachWithSingleParent, setIsDetachWithSingleParent] = useState<boolean | undefined>();

  useEffect(() => {
    fetchParentsDocumentPart(props.documentId).then((parents) => setIsDetachWithSingleParent(parents.length === 1));

    return () => LoadingManager.remove(DETACH_ELABORATION);
  }, []);

  const addedElaborationsDetails = useAppSelector((store) => store.remoteElaborations.addedElaborationItemsList);
  const blockingElabFindFunction = useCallback(
    (item) => item.elaborationType === "DETACH" && elaborationStatusItemIsBlocking(item, addedElaborationsDetails),
    [addedElaborationsDetails]
  );
  const blockingElaboration = useFilteredElaborations({
    findFunction: blockingElabFindFunction,
  });
  return (
    <ElaborationWrapper notificationKey={DETACH_ELABORATION} elaborationItem={blockingElaboration}>
      <>
        <HeaderPanel data-testid={"header"} applyPadding>
          {formatMessage("cof.documentmanagement.outline.detach.tile.detachdocument")}
        </HeaderPanel>

        {isDetachWithSingleParent !== undefined && (
          <EditDocument
            onLastBackButton={() => props.onCloseModal()}
            isEdit={isDetachWithSingleParent}
            editDocumentOperationType={isDetachWithSingleParent ? "DETACHFAKE" : "DETACH"}
            onSave={(data) => detachDocument(data)}
            getDocumentPartDetails={async () => {
              const documentMetadata = await fetchDocumentMetadata(props.documentId);
              const documentPart = await fetchDocumentPartDetails(props.documentId);
              if (isDetachWithSingleParent)
                return {
                  documentData: {
                    description: documentPart.description,
                    name: documentPart.name,
                    root: false,
                    multilinked: false,
                    path: documentPart.path,
                    documentId: documentPart.id,
                    advanced: {
                      ...defaultFormatting,
                      templateInfo: documentMetadata.templateInfo,
                      locale: documentMetadata.formatMetadata?.locale,
                    },
                    language: documentMetadata.language,
                    parameters: documentMetadata.parametersMetadata,
                  },
                  fatherDocumentParameters: null,
                };
              return {
                sourceDocumentName: documentPart.name,
                documentData: {
                  description: documentPart.description,
                  name: "",
                  root: false,
                  multilinked: false,
                  path: "",
                  documentId: documentPart.id,
                  advanced: {
                    ...defaultFormatting,
                    templateInfo: documentMetadata.templateInfo,
                    locale: documentMetadata.formatMetadata?.locale,
                  },
                  language: documentMetadata.language,
                  parameters: documentMetadata.parametersMetadata ?? [],
                },
                fatherDocumentParameters: null,
              };
            }}
          />
        )}
      </>
    </ElaborationWrapper>
  );
};
